import { areIntervalsOverlapping } from 'date-fns'

import { generateConflictErrorMessages } from './helpers/messageHelpers'
import { getLineItemsWithInventoryOverlap, getLineItemTargetedInventory } from './helpers/lineItemHelpers'
import { generateDateRange } from './helpers/dateHelpers'
import { environmentTypes } from '../fields'

type EnvironmentType = (typeof environmentTypes)[keyof typeof environmentTypes]

interface LineItem {
  date_start: string
  date_stop: string
  name: string
  [key: string]: any
}

interface Placement {
  placement_id: number
  [key: string]: any
}

interface DateOverlap {
  existingStart: string
  existingEnd: string
  proposedStart: string
  proposedEnd: string
}

interface Conflict {
  lineItem: LineItem
  conflictingInventoryIds: number[]
  dateOverlap: DateOverlap
}

interface ValidationResult {
  isValid: boolean
  errors: string[]
  conflicts?: Conflict[]
}

interface FindConflictingLineItemsParams {
  existingLineItems: LineItem[]
  proposedStartDate: string
  proposedEndDate: string
  proposedInventoryIds: number[]
  environmentType: EnvironmentType
}

interface ValidateNoOverlappingParams {
  existingLineItems: LineItem[]
  proposedStartDate: string
  proposedEndDate: string
  proposedInventoryIds: number[]
  allInventory?: Placement[]
  environmentType: EnvironmentType
}

interface DatesWithInventoryOverlapParams {
  existingLineItems: LineItem[]
  proposedInventoryIds: number[]
  environmentType: EnvironmentType
}

export const findConflictingLineItems = ({
  existingLineItems,
  proposedStartDate,
  proposedEndDate,
  proposedInventoryIds,
  environmentType
}: FindConflictingLineItemsParams): Conflict[] => {
  const lineItemsWithInventoryOverlap = getLineItemsWithInventoryOverlap({
    existingLineItems,
    proposedInventoryIds,
    environmentType
  })

  const conflicts: Conflict[] = []

  lineItemsWithInventoryOverlap.forEach(lineItem => {
    const existingInventoryIds = getLineItemTargetedInventory(lineItem)
    // Check if date ranges overlap
    const datesOverlap = areIntervalsOverlapping(
      { start: new Date(proposedStartDate), end: new Date(proposedEndDate) },
      { start: new Date(lineItem.date_start), end: new Date(lineItem.date_stop) }
    )

    // If both dates and inventory overlap, it's a conflict
    if (datesOverlap) {
      const conflictingInventoryIds = proposedInventoryIds.filter(id => existingInventoryIds.includes(id))

      conflicts.push({
        lineItem,
        conflictingInventoryIds,
        dateOverlap: {
          existingStart: lineItem.date_start,
          existingEnd: lineItem.date_stop,
          proposedStart: proposedStartDate,
          proposedEnd: proposedEndDate
        }
      })
    }
  })

  return conflicts
}

export const validateNoOverlappingGamTargetingPerInventoryPerDay = ({
  existingLineItems,
  proposedStartDate,
  proposedEndDate,
  proposedInventoryIds,
  allInventory = [],
  environmentType
}: ValidateNoOverlappingParams): ValidationResult => {
  // If no inventory items are proposed, no conflicts possible
  if (!proposedInventoryIds || proposedInventoryIds.length === 0) {
    return { isValid: true, errors: [] }
  }

  // If no dates are provided, cannot validate but still return notValidDates
  if (!proposedStartDate || !proposedEndDate) {
    return { isValid: true, errors: [] }
  }

  const conflicts = findConflictingLineItems({
    existingLineItems,
    proposedStartDate,
    proposedEndDate,
    proposedInventoryIds,
    environmentType
  })

  if (conflicts.length === 0) {
    return { isValid: true, errors: [] }
  }

  const errorMessages = generateConflictErrorMessages(conflicts, allInventory)

  return {
    conflicts,
    isValid: false,
    errors: errorMessages
  }
}

export const getDatesWithInventoryOverlap = ({
  existingLineItems,
  proposedInventoryIds,
  environmentType
}: DatesWithInventoryOverlapParams): string[] => {
  const lineItemsWithInventoryOverlap = getLineItemsWithInventoryOverlap({
    existingLineItems,
    proposedInventoryIds,
    environmentType
  })

  const conflictDates = new Set<string>()

  lineItemsWithInventoryOverlap.forEach(lineItem => {
    // Add all dates in the existing line item's range to conflict dates
    const dates = generateDateRange(lineItem.date_start, lineItem.date_stop)
    dates.forEach(date => {
      conflictDates.add(date)
    })
  })

  return Array.from(conflictDates).sort()
}
